import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDropzone } from "react-dropzone";


interface CampSiteFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  initialName?: string;
  initialLocation?: string;
  initialCapacity?: number;
  initialDescription?: string;
  initialPrice?: number;
  initialImageUrls?: ImageUrl[];
  isEditing?: boolean;
  campSiteId?: number;
}

interface ImageUrl {
  id?: number;
  url: string;
  isMain?: boolean;
}

const CampSiteForm: React.FC<CampSiteFormProps> = ({
  onSubmit,
  onCancel,
  initialName = "",
  initialLocation = "",
  initialCapacity = 0,
  initialDescription = "",
  initialPrice = 0,
  initialImageUrls = [],
  isEditing = false,
  campSiteId,
}) => {
  const [name, setName] = useState(initialName);
  const [location, setLocation] = useState(initialLocation);
  const [capacity, setCapacity] = useState(initialCapacity);
  const [description, setDescription] = useState(initialDescription);
  const [pricePerNight, setPricePerNight] = useState(initialPrice);
  const [imageUrls, setImageUrls] = useState<ImageUrl[]>(initialImageUrls);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [mainImageIndex, setMainImageIndex] = useState<number | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [pricePerNightError, setPricePerNightError] = useState<string | null>(
    null
  );
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const locationState = useLocation().state as { isEditing: boolean };

  useEffect(() => {
    if (id) {
      // Fetch campsite details for editing
      axios
        .get<{
          name: string;
          location: string;
          capacity: number;
          description: string;
          pricePerNight: number;
          imageUrls: ImageUrl[];
        }>(`${apiUrl}/CampSite/getCampSite/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        })
        .then((response) => {
          const campsite = response.data;
          setName(campsite.name);
          setLocation(campsite.location);
          setCapacity(campsite.capacity);
          setDescription(campsite.description);
          setPricePerNight(campsite.pricePerNight);
          setImageUrls(campsite.imageUrls);
          const mainIndex = campsite.imageUrls.findIndex((img) => img.isMain);
          setMainImageIndex(mainIndex);
        })
        .catch((error) =>
          console.error("Error fetching campsite details", error)
        );
    }
  }, [id, apiUrl]);

  // Sort images to ensure the main image is first
  const sortedImageUrls = [...imageUrls].sort((a, b) => (b.isMain ? 1 : -1));

  const handleRemoveImageUrl = async (imageUrl: string, campSiteId: number) => {
    try {
      const requestUrl = `${apiUrl}/CampSite/deletecampsiteImage/${campSiteId}`;
      console.log(`Request URL: ${requestUrl}`);
      console.log(`Request Params: imageUrl=${imageUrl}`);

      const response = await axios.delete(requestUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: { imageUrl },
      });
      console.log("Image delete response from backend:", response.data);

      const newImageUrls = imageUrls.filter((image) => image.url !== imageUrl);
      setImageUrls(newImageUrls);
    } catch (error: any) {
      console.error("Error deleting image", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      }
    }
  };

  const handleFileUpload = (files: File[]) => {
    const newImageUrls = [...imageUrls];
    const newSelectedFiles = [...selectedFiles];

    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newImageUrls.push({ url: reader.result as string, isMain: false });
        newSelectedFiles.push(file);
        setImageUrls([...newImageUrls]);
        setSelectedFiles([...newSelectedFiles]);
      };
      reader.readAsDataURL(file);
    });
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFileUpload(acceptedFiles);
    },
    [imageUrls, selectedFiles]
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Reset error messages
    setNameError(null);
    setLocationError(null);
    setCapacityError(null);
    setDescriptionError(null);
    setPricePerNightError(null);

    // Validate form fields
    let isValid = true;
    if (!name) {
        setNameError("Name cannot be empty.");
        isValid = false;
    }
    if (!location) {
        setLocationError("Location cannot be empty.");
        isValid = false;
    }
    if (capacity < 5) {
        setCapacityError("Capacity must be 5 or more.");
        isValid = false;
    }
    if (!description) {
        setDescriptionError("Description cannot be empty.");
        isValid = false;
    }
    if (pricePerNight < 0) {
        setPricePerNightError("Price per night must be a positive number.");
        isValid = false;
    } else if (pricePerNight < 100) {
        setPricePerNightError("Price per night must be at least € 100.");
        isValid = false;
    } else if (pricePerNight > 10000) {
        setPricePerNightError("Price per night must be less than € 10000.");
        isValid = false;
    }

    if (!isValid) {
        return;
    }

    try {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("location", location);
        formData.append("capacity", capacity.toString());
        formData.append("description", description);
        formData.append("pricePerNight", pricePerNight.toString());
        if (mainImageIndex !== null) {
            formData.append("mainImageIndex", mainImageIndex.toString());
        }
        console.log("Main image index:", mainImageIndex);

        // Include existing images
        imageUrls.forEach((image, index) => {
            if (image.url !== undefined) {
                formData.append(`existingImages[${index}].url`, image.url);
                formData.append(
                    `existingImages[${index}].isMain`,
                    (mainImageIndex === index).toString()
                );
            }
        });

        let response: { data: { data: any } };
        if (id) {
            // Update existing campsite
            selectedFiles.forEach((file) => {
                formData.append("newImages", file);
            });
            response = await axios.put(
                `${apiUrl}/CampSite/updateCampSite/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    },
                }
            );
        } else {
            // Add new campsite
            selectedFiles.forEach((file) => {
                formData.append("images", file);
            });
            response = await axios.post(
                `${apiUrl}/CampSite/addCampSite`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    },
                }
            );
        }

        console.log("Campsite response from backend:", response.data);
        onSubmit(response.data.data);
        navigate("/campsites");
    } catch (error: any) {
        console.error("Error submitting campsite", error);
        if (error.response) {
            console.error("Response data:", error.response.data);
            console.error("Response status:", error.response.status);
            console.error("Response headers:", error.response.headers);
        }
    }
};


  const handleCancel = () => {
    navigate("/campsites");
  };

  return (
    <section className="container campsiteform-section mb-5 pb-5">
      <form onSubmit={handleSubmit} className="campsiteform">
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            Name
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError && <div className="error">{nameError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="location" className="form-label">
            Location
          </label>
          <input
            type="text"
            className="form-control"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          {locationError && <div className="error">{locationError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="capacity" className="form-label">
            Capacity
          </label>
          <input
            type="number"
            className="form-control"
            id="capacity"
            value={capacity}
            onChange={(e) => setCapacity(Number(e.target.value))}
          />
          {capacityError && <div className="error">{capacityError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="description" className="form-label">
            Description
          </label>
          <textarea
            className="form-control"
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Write something about this site..."
          />
          {descriptionError && <div className="error">{descriptionError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="price" className="form-label">
            Price per Night
          </label>
          <input
            type="number"
            className="form-control"
            id="price"
            value={pricePerNight}
            onChange={(e) => setPricePerNight(Number(e.target.value) || 0)}
          />
          {pricePerNightError && (
            <div className="error">{pricePerNightError}</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Images</label>
          {sortedImageUrls.map((image, index) => (
            <div key={index} className="input-group">
              <img
                src={image.url}
                alt={`Campsite ${index + 1}`}
                className={`img-thumbnail ${image.isMain ? "border border-primary" : ""}`}
              />
              <button
                type="button"
                className="button button-danger"
                onClick={() => handleRemoveImageUrl(image.url, campSiteId || 0)}
              >
                Remove
              </button>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="mainImage"
                  id={`mainImage${index}`}
                  checked={mainImageIndex === index}
                  onChange={() => {
                    setMainImageIndex(index);
                    const updatedImageUrls = imageUrls.map((img, imgIndex) => ({
                      ...img,
                      isMain: imgIndex === index,
                    }));
                    setImageUrls(updatedImageUrls);
                  }}
                />
                <label
                  className="form-check-label"
                  htmlFor={`mainImage${index}`}
                >
                  Main Image
                </label>
              </div>
            </div>
          ))}
          {imageUrls.length < 5 && (
            <div
              {...getRootProps({ className: "dropzone" })}
              className="input-group"
            >
              <input {...getInputProps()} />
              <p>Drag 'n' drop some files here, or click to select files</p>
            </div>
          )}
        </div>
        <div className="button-area mb-5 pb-5">
          <button type="submit" className="btn btn-outline-success">
            {locationState.isEditing ? "Update" : "Add"} Campsite
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
};

export default CampSiteForm;
